import {
  addDays,
  addMonths,
  addWeeks,
  differenceInDays,
  endOfMonth,
  endOfWeek,
  format,
  parseISO,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';

import { DynamicDateRangeType } from '@/types/dynamicDateRange';

export const calculateDynamicDateRange = (
  dynamicType: DynamicDateRangeType,
  originalStart?: string,
  originalEnd?: string,
): { dateFrom: string; dateTo: string } => {
  const today = new Date();

  let startDate: Date;
  let endDate: Date;

  switch (dynamicType) {
    case 'thisWeek':
      startDate = startOfWeek(today, { weekStartsOn: 1 });
      endDate = endOfWeek(today, { weekStartsOn: 1 });
      break;

    case 'nextWeek':
      startDate = startOfWeek(addWeeks(today, 1), { weekStartsOn: 1 });
      endDate = endOfWeek(addWeeks(today, 1), { weekStartsOn: 1 });
      break;

    case 'lastWeek':
      startDate = startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 });
      endDate = endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 });
      break;

    case 'thisMonth':
      startDate = startOfMonth(today);
      endDate = endOfMonth(today);
      break;

    case 'nextMonth':
      startDate = startOfMonth(addMonths(today, 1));
      endDate = endOfMonth(addMonths(today, 1));
      break;

    case 'lastMonth':
      startDate = startOfMonth(subMonths(today, 1));
      endDate = endOfMonth(subMonths(today, 1));
      break;

    case 'last7Days':
      endDate = today;
      startDate = subDays(today, 6);
      break;

    case 'last30Days':
      endDate = today;
      startDate = subDays(today, 29);
      break;

    case 'today':
      startDate = today;
      endDate = today;
      break;

    case 'tomorrow':
      startDate = addDays(today, 1);
      endDate = addDays(today, 1);
      break;

    case 'yesterday':
      startDate = subDays(today, 1);
      endDate = subDays(today, 1);
      break;

    default:
      startDate = today;
      endDate = today;
  }

  if (
    originalStart &&
    originalEnd &&
    (dynamicType === 'thisWeek' || dynamicType === 'thisMonth')
  ) {
    const originalStartDate = parseISO(originalStart);
    const originalEndDate = parseISO(originalEnd);
    const originalDuration = differenceInDays(
      originalEndDate,
      originalStartDate,
    );

    if (originalDuration > 0) {
      endDate = addDays(startDate, originalDuration);
    }
  }

  return {
    dateFrom: format(startDate, 'yyyy-MM-dd'),
    dateTo: format(endDate, 'yyyy-MM-dd'),
  };
};

export const getDynamicDateRangeLabel = (
  dynamicType: DynamicDateRangeType,
): string => {
  const labels: Record<DynamicDateRangeType, string> = {
    today: 'Today',
    tomorrow: 'Tomorrow',
    yesterday: 'Yesterday',
    thisWeek: 'This Week',
    nextWeek: 'Next Week',
    lastWeek: 'Last Week',
    thisMonth: 'This Month',
    nextMonth: 'Next Month',
    lastMonth: 'Last Month',
    last7Days: 'Last 7 Days',
    last30Days: 'Last 30 Days',
  };

  return labels[dynamicType];
};

export const calculateUnionDynamicDateRange = (
  dynamicTypes: DynamicDateRangeType[],
  originalStart?: string,
  originalEnd?: string,
): { dateFrom: string; dateTo: string } => {
  const ranges = dynamicTypes.map((t) =>
    calculateDynamicDateRange(t, originalStart, originalEnd),
  );
  return {
    dateFrom: ranges.reduce(
      (min, r) => (r.dateFrom < min ? r.dateFrom : min),
      ranges[0].dateFrom,
    ),
    dateTo: ranges.reduce(
      (max, r) => (r.dateTo > max ? r.dateTo : max),
      ranges[0].dateTo,
    ),
  };
};
